import { useQuery } from '@tanstack/react-query';
import { api } from '../client';
import { AUTH_USER_TOKEN_KEY } from '../../../constants';
import moment from 'moment';
import { useRef } from 'react';
import { IUserData } from 'types/user';

export type UserDataRawResponse = {
  data: IUserData;
  error: boolean;
};

export type UserDataResponse = IUserData;

export const USER_QUERY_KEY = 'user';

export interface IUserProfile {
  userData: UserDataResponse | undefined;
  companyId: number | undefined;
  isLoading: boolean;
  error: Error | null;
}

export const useUserProfile = (
  params: {
    enabled?: boolean;
  } = { enabled: true }
): IUserProfile => {
  const prevUserData = useRef<UserDataResponse>();

  const {
    data: userData,
    isLoading,
    error
  } = useQuery<UserDataRawResponse, Error, UserDataResponse>({
    queryKey: [USER_QUERY_KEY],
    queryFn: async () => {
      const { data } = await api.get<UserDataRawResponse>('/identity/me');
      return data;
    },
    select: (response) => response.data,
    enabled: !!localStorage.getItem(AUTH_USER_TOKEN_KEY) && params?.enabled,
    staleTime: Infinity, // Data will never become stale
    gcTime: Infinity // Data will never be garbage collected
  });

  if (userData && !prevUserData.current) {
    // initialize timezone first time user data is loaded
    moment.tz.setDefault(userData.timezone);
    prevUserData.current = userData;
  }

  return {
    userData,
    companyId: userData?.companyId,
    isLoading,
    error
  };
};
